import React from "react";
import { cn } from "@/lib/utils";

export function SectionLabel({ children, className, right }) {
  return (
    <div className={cn("flex items-center justify-between mb-3", className)}>
      <span className="label-caps">{children}</span>
      {right}
    </div>
  );
}

export function PageHeader({ title, subtitle, action, testId }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6" data-testid={testId}>
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#FAFAFA]">{title}</h1>
        {subtitle && <p className="text-[#A3A3A3] mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Panel({ children, className, testId }) {
  return (
    <div data-testid={testId} className={cn("bg-surface border border-edge rounded-xl", className)}>
      {children}
    </div>
  );
}

export function MetricCard({ label, value, sub, accent, onClick, testId, icon: Icon }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "text-left bg-surface border border-edge rounded-xl p-5 w-full transition-colors duration-150",
        onClick && "hover:bg-surface2 hover:border-gold/30 cursor-pointer"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="label-caps">{label}</span>
        {Icon && <Icon size={16} className="text-[#4b4b4b]" />}
      </div>
      <div className="mt-3 font-num text-3xl sm:text-4xl tracking-tight" style={{ color: accent || "#FAFAFA" }}>
        {value}
      </div>
      {sub && <div className="text-xs text-[#A3A3A3] mt-1">{sub}</div>}
    </button>
  );
}

export function EmptyState({ title, hint, action, testId }) {
  return (
    <div data-testid={testId} className="border border-dashed border-edge rounded-xl p-8 text-center">
      <p className="text-[#FAFAFA] font-medium">{title}</p>
      {hint && <p className="text-sm text-[#A3A3A3] mt-1">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function StatusDot({ color, className }) {
  return <span className={cn("inline-block w-2 h-2 rounded-full", className)} style={{ backgroundColor: color }} />;
}

export function Pill({ children, color = "#A3A3A3", solid, className, ...rest }) {
  return (
    <span
      {...rest}
      className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap", className)}
      style={solid
        ? { backgroundColor: color, color: "#0A0A0A" }
        : { backgroundColor: `${color}1f`, color, border: `1px solid ${color}44` }}
    >
      {children}
    </span>
  );
}

export function Labeled({ label, children, className }) {
  return (
    <label className={cn("block", className)}>
      <span className="label-caps block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center gap-3 text-[#A3A3A3] py-16 justify-center">
      <span className="w-3 h-3 rounded-full bg-gold animate-pulse" />
      <span className="text-sm">{label}</span>
    </div>
  );
}
